// apps/spa/src/routes/RemindersList.tsx
// Real /reminders index route. Replaces the 03-01 placeholder.
//
// Renders:
//   * page header 'Reminders' (Fraunces) + '+ New reminder' button (top-right)
//   * Skeleton rows while useReminders().isLoading
//   * <EmptyState /> when reminders.length === 0
//   * <ReminderTable /> otherwise
//   * <DeleteReminderDialog /> behind a local state slot keyed by selected id

import { useState } from 'react';
import { Link } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useChannels } from '@/hooks/useChannels';
import { useReminders } from '@/hooks/useReminders';
import { DeleteReminderDialog } from '@/components/reminders/DeleteReminderDialog';
import { EmptyState } from '@/components/reminders/EmptyState';
import { ReminderTable } from '@/components/reminders/ReminderTable';
import { fadeUp } from '@/lib/motion';

export function RemindersList() {
  const reminders = useReminders();
  const channels = useChannels();
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const reduce = useReducedMotion();

  const target =
    reminders.data?.find((r) => r.id === deleteTargetId) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary/80">Reminders</p>
          <h1 className="font-display text-3xl">Reminders</h1>
        </div>
        <Link to="/reminders/new">
          <Button>+ New reminder</Button>
        </Link>
      </div>

      {reminders.isLoading ? (
        <div className="glass p-2 sm:p-4 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full skel-shimmer" />
          ))}
        </div>
      ) : (reminders.data?.length ?? 0) === 0 ? (
        <EmptyState />
      ) : (
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : 'hidden'}
          animate="show"
          className="glass p-2 sm:p-4"
        >
          <ReminderTable
            reminders={reminders.data ?? []}
            channels={channels.data ?? []}
            onDelete={setDeleteTargetId}
          />
        </motion.div>
      )}

      <DeleteReminderDialog
        reminder={target}
        open={!!target}
        onClose={() => setDeleteTargetId(null)}
      />
    </div>
  );
}
